import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, FileText, Plus, Trash2, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/common/ConfirmDialog';

const BLANK_ENTITY = {
  name: '', email: '', phone: '',
  address_street: '', address_city: '', address_state: '', address_zip: '',
  logo_url: '',
  // optional invoice config
  invoice_number_format: '', invoice_payment_terms: '', invoice_notes_terms: '', invoice_footer: '',
  invoice_title_items: '', invoice_title_units: '', invoice_title_price: '',
};

const InvoiceSettingsTab = ({ tenantId }) => {
  const { isTenantAdmin } = useAuth();
  const isAdmin = isTenantAdmin();
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invoice_number_format: 'INV-{year}-{number}',
    invoice_payment_terms: '30_days',
    invoice_notes_terms: '',
    invoice_footer: '',
    invoice_title_items: 'Items',
    invoice_title_units: 'Units',
    invoice_title_price: 'Price',
    default_expense_markup_percent: '',
  });

  // Secondary billing entities
  const [billingEntities, setBillingEntities] = useState([]);
  const [entityForm, setEntityForm] = useState(null);
  const [entityConfigOpen, setEntityConfigOpen] = useState(false);
  const [savingEntity, setSavingEntity] = useState(false);
  const [expandedEntity, setExpandedEntity] = useState(null);

  const fetchBillingEntities = useCallback(async () => {
    try {
      const r = await api.get('/billing-entities');
      setBillingEntities(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchBillingEntities();
  }, [tenantId, fetchBillingEntities]);

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const settings = response.data.settings || {};
      setForm({
        invoice_number_format: settings.invoice_number_format || 'INV-{year}-{number}',
        invoice_payment_terms: settings.invoice_payment_terms || '30_days',
        invoice_notes_terms: settings.invoice_notes_terms || '',
        invoice_footer: settings.invoice_footer || '',
        invoice_title_items: settings.invoice_title_items || 'Items',
        invoice_title_units: settings.invoice_title_units || 'Units',
        invoice_title_price: settings.invoice_title_price || 'Price',
        default_expense_markup_percent: settings.default_expense_markup_percent ?? '',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load invoice settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const currentSettings = response.data.settings || {};
      
      await api.put(`/tenants/${tenantId}`, {
        settings: {
          ...currentSettings,
          ...form,
          default_expense_markup_percent: form.default_expense_markup_percent !== '' ? parseFloat(form.default_expense_markup_percent) : null,
        }
      });
      toast.success('Invoice settings updated');
      fetchSettings();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Invoice Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Invoice Number Format</Label>
              <Input
                value={form.invoice_number_format}
                onChange={(e) => setForm({...form, invoice_number_format: e.target.value})}
                placeholder="INV-{year}-{number}"
              />
              <p className="text-xs text-muted-foreground">
                Use {'{'}year{'}'} and {'{'}number{'}'} as placeholders
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Select
                value={form.invoice_payment_terms}
                onValueChange={(value) => setForm({...form, invoice_payment_terms: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upon_receipt">Upon Receipt</SelectItem>
                  <SelectItem value="15_days">Net 15</SelectItem>
                  <SelectItem value="30_days">Net 30</SelectItem>
                  <SelectItem value="45_days">Net 45</SelectItem>
                  <SelectItem value="60_days">Net 60</SelectItem>
                  <SelectItem value="90_days">Net 90</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Items Column Title</Label>
              <Input
                value={form.invoice_title_items}
                onChange={(e) => setForm({...form, invoice_title_items: e.target.value})}
                placeholder="Items"
              />
            </div>
            <div className="space-y-2">
              <Label>Units Column Title</Label>
              <Input
                value={form.invoice_title_units}
                onChange={(e) => setForm({...form, invoice_title_units: e.target.value})}
                placeholder="Units"
              />
            </div>
            <div className="space-y-2">
              <Label>Price Column Title</Label>
              <Input
                value={form.invoice_title_price}
                onChange={(e) => setForm({...form, invoice_title_price: e.target.value})}
                placeholder="Price"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Expense Markup % <span className="text-xs text-muted-foreground">(FarmFolio)</span></Label>
              <Input
                type="number" min="0" max="100" step="0.1"
                value={form.default_expense_markup_percent}
                onChange={(e) => setForm({...form, default_expense_markup_percent: e.target.value})}
                placeholder="e.g. 15"
                data-testid="default-expense-markup-input"
              />
              <p className="text-xs text-muted-foreground">Fallback markup for farm-paid vendor costs when no account- or item-level markup is set.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Invoice Notes/Terms</Label>
            <Textarea
              value={form.invoice_notes_terms}
              onChange={(e) => setForm({...form, invoice_notes_terms: e.target.value})}
              placeholder="Payment terms, conditions, or notes..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Invoice Footer</Label>
            <Textarea
              value={form.invoice_footer}
              onChange={(e) => setForm({...form, invoice_footer: e.target.value})}
              placeholder="Thank you for your business..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Secondary Billing Entity — admin only, discreet, max 1 */}
      {isAdmin && (
        <div className="pt-4 border-t border-dashed border-border">
          {billingEntities.length === 0 && entityForm === null ? (
            <button
              onClick={async () => { setEntityForm({ ...BLANK_ENTITY }); setEntityConfigOpen(false); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add additional billing entity
            </button>
          ) : billingEntities.length > 0 && (entityForm === null || entityForm?.id) ? (
          <div className="space-y-3">
              {billingEntities.map(ent => (
                <Card key={ent.id} className="border-dashed">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <button
                        className="flex items-center gap-2 text-left flex-1 min-w-0"
                        onClick={() => setExpandedEntity(expandedEntity === ent.id ? null : ent.id)}
                      >
                        {expandedEntity === ent.id
                          ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                        <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-semibold truncate">{ent.name}</span>
                      </button>
                      <div className="flex gap-2 ml-2 shrink-0">
                        {entityForm?.id !== ent.id && (
                          <Button variant="outline" size="sm" className="h-7 text-xs"
                            onClick={() => { setEntityForm({ ...BLANK_ENTITY, ...ent }); setEntityConfigOpen(false); setExpandedEntity(null); }}>
                            Edit
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={async () => {
                            if (!await confirm({ title: 'Remove this billing entity? Existing invoices will not be affected.', destructive: true })) return;
                            await api.delete(`/billing-entities/${ent.id}`);
                            if (entityForm?.id === ent.id) setEntityForm(null);
                            setExpandedEntity(null);
                            fetchBillingEntities();
                            toast.success('Billing entity removed');
                          }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Read-only expanded view */}
                  {expandedEntity === ent.id && entityForm?.id !== ent.id && (
                    <CardContent className="pt-0 pb-3 px-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs border-t pt-3">
                        {ent.email && <div><span className="text-muted-foreground">Email: </span><span>{ent.email}</span></div>}
                        {ent.phone && <div><span className="text-muted-foreground">Phone: </span><span>{ent.phone}</span></div>}
                        {ent.address_street && <div className="col-span-2"><span className="text-muted-foreground">Address: </span><span>{[ent.address_street, ent.address_city, ent.address_state, ent.address_zip].filter(Boolean).join(', ')}</span></div>}
                        {ent.logo_url && (
                          <div className="col-span-2 mt-1">
                            <span className="text-muted-foreground block mb-1">Logo:</span>
                            <img src={ent.logo_url} alt="Logo" className="h-8 w-auto object-contain border rounded" />
                          </div>
                        )}
                        {(ent.invoice_number_format || ent.invoice_payment_terms || ent.invoice_notes_terms || ent.invoice_footer) && (
                          <div className="col-span-2 mt-1 pt-2 border-t space-y-1">
                            <p className="text-muted-foreground font-medium uppercase tracking-wide text-[10px]">Invoice Config</p>
                            {ent.invoice_number_format && <div><span className="text-muted-foreground">Number format: </span><span>{ent.invoice_number_format}</span></div>}
                            {ent.invoice_payment_terms && <div><span className="text-muted-foreground">Payment terms: </span><span>{ent.invoice_payment_terms.replace('_days', ' days').replace('_', ' ')}</span></div>}
                            {ent.invoice_notes_terms && <div><span className="text-muted-foreground">Notes/Terms: </span><span className="whitespace-pre-wrap">{ent.invoice_notes_terms}</span></div>}
                            {ent.invoice_footer && <div><span className="text-muted-foreground">Footer: </span><span className="whitespace-pre-wrap">{ent.invoice_footer}</span></div>}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}

                  {/* Inline edit form — shown when this entity is being edited */}
                  {entityForm?.id === ent.id && (
                    <CardContent className="pt-0 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Organization Name *</Label>
                          <Input value={entityForm.name} onChange={e => setEntityForm(f => ({...f, name: e.target.value}))} placeholder="Second Entity LLC" className="h-8 text-sm" />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Billing Email</Label>
                          <Input value={entityForm.email} onChange={e => setEntityForm(f => ({...f, email: e.target.value}))} className="h-8 text-sm" />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Phone</Label>
                          <Input value={entityForm.phone || ''} onChange={e => setEntityForm(f => ({...f, phone: e.target.value}))} className="h-8 text-sm" />
                        </div>
                        <div className="space-y-1 col-span-2"><Label className="text-xs">Street Address</Label><Input value={entityForm.address_street} onChange={e => setEntityForm(f => ({...f, address_street: e.target.value}))} className="h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs">City</Label><Input value={entityForm.address_city} onChange={e => setEntityForm(f => ({...f, address_city: e.target.value}))} className="h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs">State / ZIP</Label>
                          <div className="flex gap-2">
                            <Input value={entityForm.address_state} onChange={e => setEntityForm(f => ({...f, address_state: e.target.value}))} placeholder="KY" className="h-8 text-sm w-16" />
                            <Input value={entityForm.address_zip} onChange={e => setEntityForm(f => ({...f, address_zip: e.target.value}))} placeholder="40507" className="h-8 text-sm flex-1" />
                          </div>
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Logo</Label>
                          <div className="flex items-center gap-3">
                            <label className="cursor-pointer">
                              <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors">
                                <FileText className="w-3 h-3" />{entityForm.logo_url ? 'Change Logo' : 'Upload Logo'}
                              </span>
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                try {
                                  const fd = new FormData(); fd.append('file', file);
                                  const r = await api.post(`/billing-entities/${entityForm.id}/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                  setEntityForm(f => ({ ...f, logo_url: r.data.logo_url }));
                                  toast.success('Logo uploaded');
                                } catch { toast.error('Failed to upload logo'); }
                              }} />
                            </label>
                            {entityForm.logo_url && (
                              <div className="flex items-center gap-2">
                                <img src={entityForm.logo_url} alt="Logo" className="h-8 w-auto object-contain border rounded" />
                                <button className="text-xs text-destructive hover:underline" onClick={() => setEntityForm(f => ({ ...f, logo_url: '' }))}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={() => setEntityConfigOpen(o => !o)}
                          className="mr-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                          {entityConfigOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          Invoice configuration {entityConfigOpen ? '' : '(optional)'}
                        </button>
                      </div>
                      {entityConfigOpen && (
                        <div className="border-t pt-3 space-y-3">
                          <p className="text-xs text-muted-foreground">Leave blank to inherit the primary organization's settings.</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs">Invoice Number Format</Label>
                              <Input value={entityForm.invoice_number_format} onChange={e => setEntityForm(f => ({...f, invoice_number_format: e.target.value}))} placeholder="e.g. BG-{year}-{number}" className="h-8 text-sm" />
                            </div>
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs">Payment Terms</Label>
                              <Select value={entityForm.invoice_payment_terms || 'inherit'} onValueChange={v => setEntityForm(f => ({...f, invoice_payment_terms: v === 'inherit' ? '' : v}))}>
                                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Inherit from primary" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inherit">Inherit from primary</SelectItem>
                                  <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                                  <SelectItem value="15_days">Net 15</SelectItem>
                                  <SelectItem value="30_days">Net 30</SelectItem>
                                  <SelectItem value="45_days">Net 45</SelectItem>
                                  <SelectItem value="60_days">Net 60</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs">Notes / Terms</Label>
                              <Textarea value={entityForm.invoice_notes_terms} onChange={e => setEntityForm(f => ({...f, invoice_notes_terms: e.target.value}))} placeholder="Inherit from primary..." rows={2} className="text-sm" />
                            </div>
                            <div className="space-y-1 col-span-2">
                              <Label className="text-xs">Invoice Footer</Label>
                              <Textarea value={entityForm.invoice_footer} onChange={e => setEntityForm(f => ({...f, invoice_footer: e.target.value}))} placeholder="Inherit from primary..." rows={2} className="text-sm" />
                            </div>
                            <div className="grid grid-cols-3 gap-3 col-span-2">
                              <div className="space-y-1"><Label className="text-xs">Items Column Title</Label><Input value={entityForm.invoice_title_items} onChange={e => setEntityForm(f => ({...f, invoice_title_items: e.target.value}))} placeholder="Items" className="h-8 text-sm" /></div>
                              <div className="space-y-1"><Label className="text-xs">Units Column Title</Label><Input value={entityForm.invoice_title_units} onChange={e => setEntityForm(f => ({...f, invoice_title_units: e.target.value}))} placeholder="Units" className="h-8 text-sm" /></div>
                              <div className="space-y-1"><Label className="text-xs">Price Column Title</Label><Input value={entityForm.invoice_title_price} onChange={e => setEntityForm(f => ({...f, invoice_title_price: e.target.value}))} placeholder="Price" className="h-8 text-sm" /></div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => { setEntityForm(null); setEntityConfigOpen(false); }}>Cancel</Button>
                        <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" disabled={savingEntity}
                          onClick={async () => {
                            if (!entityForm.name.trim()) { toast.error('Entity name is required'); return; }
                            setSavingEntity(true);
                            // Strip non-serialisable fields before PUT
                            const { _logoFile, ...payload } = entityForm;
                            try {
                              await api.put(`/billing-entities/${entityForm.id}`, payload);
                              toast.success('Billing entity updated');
                              setEntityForm(null); setEntityConfigOpen(false); fetchBillingEntities();
                            } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
                            setSavingEntity(false);
                          }}>
                          {savingEntity ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
              {billingEntities.length < 1 && (
                <button onClick={async () => { setEntityForm({ ...BLANK_ENTITY }); setEntityConfigOpen(false); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add additional billing entity
                </button>
              )}
            </div>
          ) : entityForm !== null && !entityForm.id ? (
            <Card className="border-dashed">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  {entityForm.id ? 'Edit Billing Entity' : 'New Billing Entity'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Organization Name *</Label>
                    <Input value={entityForm.name} onChange={e => setEntityForm(f => ({...f, name: e.target.value}))} placeholder="Second Entity LLC" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Billing Email</Label>
                    <Input value={entityForm.email} onChange={e => setEntityForm(f => ({...f, email: e.target.value}))} placeholder="billing@entity.com" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Phone</Label>
                    <Input value={entityForm.phone || ''} onChange={e => setEntityForm(f => ({...f, phone: e.target.value}))} placeholder="555-123-4567" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1 col-span-2"><Label className="text-xs">Street Address</Label><Input value={entityForm.address_street} onChange={e => setEntityForm(f => ({...f, address_street: e.target.value}))} className="h-8 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">City</Label><Input value={entityForm.address_city} onChange={e => setEntityForm(f => ({...f, address_city: e.target.value}))} className="h-8 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">State / ZIP</Label>
                    <div className="flex gap-2">
                      <Input value={entityForm.address_state} onChange={e => setEntityForm(f => ({...f, address_state: e.target.value}))} placeholder="KY" className="h-8 text-sm w-16" />
                      <Input value={entityForm.address_zip} onChange={e => setEntityForm(f => ({...f, address_zip: e.target.value}))} placeholder="40507" className="h-8 text-sm flex-1" />
                    </div>
                  </div>

                  {/* Logo upload */}
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Logo</Label>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer">
                        <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors">
                          <FileText className="w-3 h-3" />
                          {entityForm.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </span>
                        <input type="file" accept="image/*" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            // If entity already saved, upload immediately; else preview via base64
                            if (entityForm.id) {
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const r = await api.post(`/billing-entities/${entityForm.id}/upload-logo`, fd, {
                                  headers: { 'Content-Type': 'multipart/form-data' },
                                });
                                setEntityForm(f => ({ ...f, logo_url: r.data.logo_url }));
                                toast.success('Logo uploaded');
                              } catch { toast.error('Failed to upload logo'); }
                            } else {
                              // Preview only — will upload after entity is created
                              const reader = new FileReader();
                              reader.onload = ev => setEntityForm(f => ({ ...f, logo_url: ev.target.result, _logoFile: file }));
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                      {entityForm.logo_url && (
                        <div className="flex items-center gap-2">
                          <img src={entityForm.logo_url} alt="Logo preview"
                            className="h-8 w-auto object-contain border rounded" />
                          <button className="text-xs text-destructive hover:underline"
                            onClick={() => setEntityForm(f => ({ ...f, logo_url: '', _logoFile: null }))}>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Max 5 MB · JPG or PNG</p>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  {/* Collapsible invoice config */}
                  <button type="button" onClick={() => setEntityConfigOpen(o => !o)}
                    className="mr-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {entityConfigOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    Invoice configuration {entityConfigOpen ? '' : '(optional)'}
                  </button>
                </div>
                {entityConfigOpen && (
                  <div className="border-t pt-3 mt-1 space-y-3">
                    <p className="text-xs text-muted-foreground">Leave blank to inherit the primary organization's settings.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs">Invoice Number Format</Label>
                        <Input value={entityForm.invoice_number_format} onChange={e => setEntityForm(f => ({...f, invoice_number_format: e.target.value}))} placeholder="e.g. BG-{year}-{number}" className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs">Payment Terms</Label>
                        <Select value={entityForm.invoice_payment_terms || 'inherit'} onValueChange={v => setEntityForm(f => ({...f, invoice_payment_terms: v === 'inherit' ? '' : v}))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Inherit from primary" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Inherit from primary</SelectItem>
                            <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                            <SelectItem value="15_days">Net 15</SelectItem>
                            <SelectItem value="30_days">Net 30</SelectItem>
                            <SelectItem value="45_days">Net 45</SelectItem>
                            <SelectItem value="60_days">Net 60</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs">Notes / Terms</Label>
                        <Textarea value={entityForm.invoice_notes_terms} onChange={e => setEntityForm(f => ({...f, invoice_notes_terms: e.target.value}))} placeholder="Inherit from primary..." rows={2} className="text-sm" />
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs">Invoice Footer</Label>
                        <Textarea value={entityForm.invoice_footer} onChange={e => setEntityForm(f => ({...f, invoice_footer: e.target.value}))} placeholder="Inherit from primary..." rows={2} className="text-sm" />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Items Column Title</Label>
                          <Input value={entityForm.invoice_title_items} onChange={e => setEntityForm(f => ({...f, invoice_title_items: e.target.value}))} placeholder="Items" className="h-8 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Units Column Title</Label>
                          <Input value={entityForm.invoice_title_units} onChange={e => setEntityForm(f => ({...f, invoice_title_units: e.target.value}))} placeholder="Units" className="h-8 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Price Column Title</Label>
                          <Input value={entityForm.invoice_title_price} onChange={e => setEntityForm(f => ({...f, invoice_title_price: e.target.value}))} placeholder="Price" className="h-8 text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => { setEntityForm(null); setEntityConfigOpen(false); }}>Cancel</Button>
                  <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" disabled={savingEntity}
                    onClick={async () => {
                      if (!entityForm.name.trim()) { toast.error('Entity name is required'); return; }
                      setSavingEntity(true);
                      const { _logoFile, ...payload } = entityForm;
                      try {
                        const created = await api.post('/billing-entities', payload);
                        if (_logoFile && created.data?.id) {
                          try {
                            const fd = new FormData();
                            fd.append('file', _logoFile);
                            await api.post(`/billing-entities/${created.data.id}/upload-logo`, fd, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });
                          } catch { /* logo upload failure is non-blocking */ }
                        }
                        toast.success('Billing entity added');
                        setEntityForm(null); setEntityConfigOpen(false); fetchBillingEntities();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Failed to save');
                      }
                      setSavingEntity(false);
                    }}>
                    {savingEntity ? 'Saving...' : 'Save Entity'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default InvoiceSettingsTab;
